import type {
  PcmpProposalStudentClass,
  PcmpSubmissionCalculation,
} from '../types/pcmpProposal.types';

export interface PcmpStudentGridCategory {
  categoryId: number;
  categoryName: string;
  salesTarget: number | null;
}

type PcmpStudentBook = PcmpProposalStudentClass['books'][number];

export interface PcmpStudentGridRow {
  classId: number;
  className: string;
  studentQty: number;
  booksByCategory: Map<number, PcmpStudentBook>;
}

export interface PcmpStudentGrid {
  categories: PcmpStudentGridCategory[];
  rows: PcmpStudentGridRow[];
  totalStudents: number;
  totalsByCategory: Map<number, number>;
}

/** Class rows × category columns, categories in the calculation's order. */
export function buildPcmpStudentGrid(
  students: PcmpProposalStudentClass[],
  calculation: Pick<PcmpSubmissionCalculation, 'categoryTargets'>,
): PcmpStudentGrid {
  const categories = new Map<number, PcmpStudentGridCategory>(
    calculation.categoryTargets.map((target) => [
      target.categoryId,
      {
        categoryId: target.categoryId,
        categoryName: target.categoryName,
        salesTarget: target.salesTarget,
      },
    ]),
  );
  const totalsByCategory = new Map<number, number>();
  const rows = students.map((row) => {
    const booksByCategory = new Map<number, PcmpStudentBook>();
    for (const book of row.books) {
      booksByCategory.set(book.categoryId, book);
      if (!categories.has(book.categoryId)) {
        categories.set(book.categoryId, {
          categoryId: book.categoryId,
          categoryName: book.categoryName,
          salesTarget: null,
        });
      }
      totalsByCategory.set(
        book.categoryId,
        (totalsByCategory.get(book.categoryId) ?? 0) + book.tentativePurchaseQty,
      );
    }
    return {
      classId: row.classId,
      className: row.className ?? `Class #${row.classId}`,
      studentQty: row.studentQty,
      booksByCategory,
    };
  });
  return {
    categories: [...categories.values()],
    rows,
    totalStudents: rows.reduce((sum, row) => sum + row.studentQty, 0),
    totalsByCategory,
  };
}
